/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  partnerApi, fmtPartnerErr, clearPartnerToken, isPartnerAuthed,
} from "../../lib/partnerApi";
import {
  ArrowLeft, SignOut, Truck, Plus, Pencil, Trash, X, MapPin,
  CheckCircle, XCircle, Phone, User, Package, Clock, Lightning,
  ShieldCheck, Warning, ShoppingBag, ToggleLeft, ToggleRight, GpsFix,
  PaperPlaneTilt, Copy, WhatsappLogo, ChatCircle,
} from "@phosphor-icons/react";

const STATUS_META = {
  pending:          { label: "Awaiting assignment", color: "#8A8780", Icon: Clock },
  assigned:         { label: "Assigned",             color: "#0B2B66", Icon: User },
  picked_up:        { label: "Picked up",            color: "#C9A227", Icon: Package },
  out_for_delivery: { label: "Out for delivery",      color: "#C25D38", Icon: Truck },
  delivered:        { label: "Delivered",            color: "#1A3317", Icon: CheckCircle },
  failed:           { label: "Failed",               color: "#7A2E18", Icon: XCircle },
};

export default function PartnerDeliveries() {
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [staffForm, setStaffForm] = useState(null);  // null | {} | existing
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [linkDialog, setLinkDialog] = useState(null);    // {staff, link}

  const issueLink = async (s) => {
    try {
      const r = await partnerApi.post(
        `/service-partners/me/delivery/staff/${s.id}/magic-link`);
      setLinkDialog({ staff: s, link: r.data });
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };

  const load = async () => {
    try {
      const params = filter ? `?status=${filter}` : "";
      const [t, s] = await Promise.all([
        partnerApi.get(`/service-partners/me/delivery/tasks${params}`),
        partnerApi.get(`/service-partners/me/delivery/staff`),
      ]);
      setTasks(t.data.tasks || []);
      setCounts(t.data.counts || {});
      setStaff(s.data.staff || []);
      // refresh the selected one too
      if (selected) {
        const fresh = (t.data.tasks || []).find(x => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        clearPartnerToken();
        nav("/service-partner/login", { replace: true });
      } else toast.error(fmtPartnerErr(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    if (!isPartnerAuthed()) { nav("/service-partner/login", { replace: true }); return; }
    load();
  }, [filter]);

  const deleteStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try {
      await partnerApi.delete(`/service-partners/me/delivery/staff/${s.id}`);
      toast.success("Staff removed"); load();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };

  if (loading) return <Loading/>;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="partner-deliveries">
      <header className="bg-white border-b border-[#E5E2D9] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/service-partner/dashboard" className="inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:text-[#2D5A27]">
            <ArrowLeft size={11}/> Dashboard
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/service-partner/orders" className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <ShoppingBag size={12}/> Orders
            </Link>
            <button onClick={() => { clearPartnerToken(); nav("/service-partner/login"); }}
                     className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <SignOut size={12}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Last-mile</div>
            <h1 className="font-heading text-3xl mt-1 text-[#1A1A1A]">Deliveries</h1>
            <p className="text-sm text-[#5C5A55] mt-1.5 max-w-xl">
              Manage your courier roster and assign confirmed orders. Customer reveals a
              6-digit OTP at the door for proof of delivery.
            </p>
          </div>
        </div>

        <section className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="task-filters">
          <Chip on={!filter} onClick={() => setFilter("")} label={`All · ${Object.values(counts).reduce((a,b)=>a+b,0)}`}/>
          {Object.entries(STATUS_META).map(([s, m]) => (
            <Chip key={s} on={filter === s} onClick={() => setFilter(s)}
                   label={`${m.label} · ${counts[s] || 0}`}/>
          ))}
        </section>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
          {/* Task list */}
          <div className="space-y-2">
            {tasks.length === 0 ? (
              <div className="bg-white border border-[#E5E2D9] rounded-xl p-10 text-center text-[#8A8780]">
                No delivery tasks yet. They appear here once an order is confirmed.
              </div>
            ) : (
              tasks.map(t => {
                const m = STATUS_META[t.status] || STATUS_META.pending;
                return (
                  <button key={t.id} onClick={() => setSelected(t)}
                          data-testid={`task-row-${t.id}`}
                          className={`w-full text-left bg-white border rounded-md p-4 flex items-center gap-3 hover:border-[#2D5A27] transition ${
                            selected?.id === t.id ? "border-[#2D5A27] shadow-sm" : "border-[#E5E2D9]"
                          }`}>
                    <div className="w-10 h-10 rounded-md grid place-items-center flex-none"
                          style={{ background: m.color + "20", color: m.color }}>
                      <m.Icon size={14} weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] text-[#8A8780]">{t.order_no}</div>
                      <div className="font-heading text-base text-[#1A1A1A] truncate">{t.customer_name}</div>
                      <div className="text-[11px] text-[#5C5A55] truncate">{t.delivery_address}</div>
                      {t.staff_name && (
                        <div className="text-[10px] text-[#2D5A27] mt-0.5">
                          → {t.staff_name} · {t.staff_phone}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded"
                            style={{ background: m.color + "20", color: m.color }}>
                        {m.label}
                      </span>
                      {t.amount_to_collect > 0 && (
                        <div className="text-[10px] mt-1 text-[#C25D38] font-mono">
                          Collect ₹{t.amount_to_collect.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail / Staff panel */}
          <aside className="space-y-4">
            {selected ? (
              <TaskDetailPanel task={selected} staff={staff} onChanged={load} onClose={() => setSelected(null)}/>
            ) : (
              <StaffPanel staff={staff} onEdit={(s) => setStaffForm(s || {})}
                            onDelete={deleteStaff} onSendLink={issueLink}/>
            )}
          </aside>
        </div>

        {/* Staff edit form */}
        {staffForm && (
          <StaffForm existing={staffForm.id ? staffForm : null}
                      onClose={() => setStaffForm(null)}
                      onSaved={() => { setStaffForm(null); load(); }}/>
        )}
        {linkDialog && (
          <MagicLinkDialog data={linkDialog}
                            onClose={() => setLinkDialog(null)}/>
        )}
      </main>
    </div>
  );
}

function MagicLinkDialog({ data, onClose }) {
  const { staff, link } = data;
  const copy = () => {
    navigator.clipboard.writeText(link.auth_url);
    toast.success("Link copied — paste in any chat to share");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          onClick={onClose} data-testid="magic-link-dialog">
      <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-5"
            onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#E1EFDE] grid place-items-center text-[#2D5A27] flex-none">
            <PaperPlaneTilt size={16} weight="duotone"/>
          </div>
          <div>
            <div className="font-heading text-xl">Login link ready</div>
            <div className="text-xs text-[#5C5A55] mt-1">
              Share this with <b>{staff.name}</b> ({staff.phone}). They'll tap it on their
              phone and be signed into the courier app instantly. The link works for
              24 hours and one tap only.
            </div>
          </div>
        </div>

        <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-md p-3 break-all text-[10px] font-mono text-[#1A1A1A]"
              data-testid="magic-link-url">
          {link.auth_url}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a href={link.whatsapp_share_url} target="_blank" rel="noreferrer"
              data-testid="share-whatsapp"
              className="h-11 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#25D366] text-white text-xs font-medium">
            <WhatsappLogo size={13} weight="fill"/> WhatsApp
          </a>
          <a href={link.sms_share_url} data-testid="share-sms"
              className="h-11 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0B2B66] text-white text-xs font-medium">
            <ChatCircle size={13} weight="fill"/> SMS
          </a>
          <button onClick={copy} data-testid="copy-link"
                   className="h-11 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#E5E2D9] text-[#5C5A55] text-xs font-medium hover:border-[#2D5A27]">
            <Copy size={13}/> Copy
          </button>
        </div>

        <div className="text-[10px] text-[#5C5A55] italic bg-[#F9F4D9] border border-[#E5DBA0] rounded p-2">
          When SMS-via-Twilio gets wired (Phase 6.x), this dialog will also auto-send the
          link to {staff.phone} so you won't need to share manually.
        </div>

        <button onClick={onClose}
                 className="w-full h-10 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium">
          Done
        </button>
      </div>
    </div>
  );
}

// ============================================================
function TaskDetailPanel({ task, staff, onChanged, onClose }) {
  const m = STATUS_META[task.status] || STATUS_META.pending;
  const [assignStaffId, setAssignStaffId] = useState(task.staff_id || "");
  const [busy, setBusy] = useState(false);
  const [otpEntered, setOtpEntered] = useState("");
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const assign = async () => {
    if (!assignStaffId) { toast.error("Pick a delivery person"); return; }
    setBusy(true);
    try {
      await partnerApi.post(`/service-partners/me/delivery/tasks/${task.id}/assign`,
                              { staff_id: assignStaffId });
      toast.success("Assigned");
      onChanged();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  const dispatch = async () => {
    setBusy(true);
    try {
      // Try to get current GPS; fall back to a partner-office default if denied
      let coords = { lat: 0, lng: 0 };
      try {
        coords = await new Promise((res, rej) => {
          navigator.geolocation.getCurrentPosition(
            (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
            (err) => rej(err),
            { timeout: 5000 },
          );
        });
      } catch { /* keep default */ }
      await partnerApi.post(`/service-partners/me/delivery/tasks/${task.id}/out-for-delivery`,
                              coords);
      toast.success("Out for delivery");
      onChanged();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  const complete = async () => {
    if (otpEntered.length !== 6) { toast.error("Enter the 6-digit OTP shown by the customer"); return; }
    setBusy(true);
    try {
      let coords = {};
      try {
        coords = await new Promise((res, rej) => {
          navigator.geolocation.getCurrentPosition(
            (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
            (err) => rej(err), { timeout: 5000 });
        });
      } catch { /* keep blank */ }
      await partnerApi.post(`/service-partners/me/delivery/tasks/${task.id}/complete`,
                              { otp: otpEntered, ...coords });
      toast.success("Delivered · order completed");
      setShowCompleteModal(false);
      setOtpEntered("");
      onChanged();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  const fail = async () => {
    const reason = window.prompt("Reason for delivery failure?");
    if (!reason) return;
    setBusy(true);
    try {
      await partnerApi.post(`/service-partners/me/delivery/tasks/${task.id}/fail`,
                              { reason });
      toast.success("Marked as failed — order returned for reassignment");
      onChanged();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-xl p-5 space-y-4" data-testid="task-detail">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] text-[#8A8780]">{task.order_no}</div>
          <div className="font-heading text-xl">{task.customer_name}</div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={14}/></button>
      </div>

      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded"
            style={{ background: m.color + "20", color: m.color }}>
        <m.Icon size={11} weight="fill"/> {m.label}
      </span>

      <div className="space-y-1.5 text-xs">
        <DetRow Icon={Phone} k="Phone" v={task.customer_phone}/>
        <DetRow Icon={MapPin} k="Address" v={task.delivery_address}/>
        {task.amount_to_collect > 0 && (
          <DetRow Icon={Warning} k="Collect on delivery"
                   v={<span className="text-[#C25D38] font-mono">₹{task.amount_to_collect.toFixed(2)}</span>}/>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55] mb-1.5">Items</div>
        <div className="space-y-1 text-xs">
          {(task.items_summary || []).map((it, i) => (
            <div key={i} className="flex justify-between">
              <span>{it.name} <span className="text-[10px] text-[#8A8780]">× {it.qty}</span></span>
              <span className="text-[10px] text-[#8A8780] uppercase">{it.type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* OTP & current GPS */}
      <div className="bg-[#F0F7EE] border border-[#2D5A27]/20 rounded-md p-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#2D5A27] font-semibold">
          Delivery OTP
        </div>
        <div className="font-mono text-2xl text-[#2D5A27] tracking-[0.3em]" data-testid="task-otp">
          {task.otp}
        </div>
        <div className="text-[10px] text-[#5C5A55] mt-1">
          The customer also sees this code. Verify at the door before handing over.
        </div>
      </div>
      {task.current_lat != null && (
        <div className="text-[10px] text-[#5C5A55] flex items-center gap-1">
          <GpsFix size={10}/> Last ping {task.current_lat.toFixed(4)}, {task.current_lng.toFixed(4)}
        </div>
      )}

      {/* Action ladder */}
      <div className="space-y-2 pt-2 border-t border-[#F0ECE3]">
        {(task.status === "pending" || task.status === "assigned") && (
          <div className="space-y-2">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">
                Assign to
              </div>
              <select value={assignStaffId} onChange={e => setAssignStaffId(e.target.value)}
                       data-testid="staff-select"
                       className="w-full h-9 px-3 border border-[#E5E2D9] rounded text-sm">
                <option value="">— pick a delivery person —</option>
                {staff.filter(s => s.is_active).map(s => (
                  <option key={s.id} value={s.id}>{s.name} · {s.phone}</option>
                ))}
              </select>
            </label>
            <button onClick={assign} disabled={busy || !assignStaffId}
                     data-testid="btn-assign"
                     className="w-full h-10 rounded bg-[#0B2B66] text-white text-xs font-medium disabled:opacity-60">
              {task.staff_id ? "Reassign" : "Assign"} delivery person
            </button>
          </div>
        )}
        {task.status === "assigned" && (
          <button onClick={dispatch} disabled={busy} data-testid="btn-dispatch"
                   className="w-full h-10 rounded bg-[#C25D38] text-white text-xs font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            <Truck size={12} weight="bold"/> Mark out for delivery (captures GPS)
          </button>
        )}
        {task.status === "out_for_delivery" && (
          <>
            <button onClick={() => setShowCompleteModal(true)} disabled={busy}
                     data-testid="btn-complete"
                     className="w-full h-10 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs font-medium inline-flex items-center justify-center gap-1.5">
              <ShieldCheck size={12} weight="bold"/> Verify OTP & complete
            </button>
            <button onClick={fail} disabled={busy}
                     className="w-full h-9 rounded border border-[#F4C7B9] text-[#7A2E18] text-xs hover:bg-[#FBE7E0]">
              Mark as failed
            </button>
          </>
        )}
      </div>

      {showCompleteModal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
              onClick={() => setShowCompleteModal(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm space-y-4"
                onClick={e => e.stopPropagation()} data-testid="complete-modal">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Proof of delivery</div>
              <div className="font-heading text-xl">Enter customer's OTP</div>
              <div className="text-xs text-[#5C5A55] mt-1">
                Ask the customer to read the 6-digit code from their order screen.
              </div>
            </div>
            <input value={otpEntered} maxLength={6} inputMode="numeric"
                    onChange={e => setOtpEntered(e.target.value.replace(/\D/g, ""))}
                    data-testid="otp-input" autoFocus
                    placeholder="••••••"
                    className="w-full h-14 px-3 border border-[#E5E2D9] rounded text-3xl font-mono tracking-[0.4em] text-center outline-none focus:border-[#2D5A27]"/>
            <button onClick={complete} disabled={busy || otpEntered.length !== 6}
                     data-testid="otp-submit"
                     className="w-full h-11 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium disabled:opacity-60">
              {busy ? "Verifying…" : "Verify & mark delivered"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffPanel({ staff, onEdit, onDelete, onSendLink }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-xl p-5 space-y-4" data-testid="staff-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Courier roster</div>
          <div className="font-heading text-xl">Delivery staff</div>
        </div>
        <button onClick={() => onEdit(null)} data-testid="btn-new-staff"
                 className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-xs font-medium">
          <Plus size={11} weight="bold"/> Add
        </button>
      </div>
      {staff.length === 0 ? (
        <div className="text-xs text-[#8A8780] italic">
          No delivery staff yet. Add at least one to assign deliveries.
        </div>
      ) : (
        <div className="space-y-2">
          {staff.map(s => (
            <div key={s.id} data-testid={`staff-row-${s.id}`}
                  className="flex items-center gap-3 p-2 hover:bg-[#F7F6F2] rounded">
              <div className={`w-8 h-8 rounded-full grid place-items-center flex-none ${
                s.is_active ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#E5E2D9] text-[#8A8780]"
              }`}>
                <User size={12} weight="duotone"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1A1A1A] font-medium">{s.name}</div>
                <div className="text-[10px] text-[#5C5A55]">{s.phone}{s.vehicle ? ` · ${s.vehicle}` : ""}</div>
              </div>
              <button onClick={() => onSendLink(s)}
                       data-testid={`send-link-${s.id}`}
                       title="Send login link to courier"
                       className="text-[#2D5A27] hover:bg-[#F0F7EE] p-1.5 rounded">
                <PaperPlaneTilt size={12} weight="duotone"/>
              </button>
              <button onClick={() => onEdit(s)} className="text-[#5C5A55] p-1"><Pencil size={11}/></button>
              <button onClick={() => onDelete(s)} className="text-[#C25D38] p-1"><Trash size={11}/></button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-[#8A8780] italic border-t border-[#F0ECE3] pt-3">
        Tap <PaperPlaneTilt size={9} className="inline"/> on a staff row to send them a one-tap
        login link via WhatsApp or SMS. They'll be able to use the courier app on their own phone.
      </div>
    </div>
  );
}

function StaffForm({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(() => ({
    name: existing?.name || "",
    phone: existing?.phone || "",
    staff_type: existing?.staff_type || "pharmacy_runner",
    vehicle: existing?.vehicle || "",
    is_active: existing?.is_active ?? true,
  }));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) await partnerApi.put(`/service-partners/me/delivery/staff/${existing.id}`, form);
      else await partnerApi.post(`/service-partners/me/delivery/staff`, form);
      toast.success(isEdit ? "Updated" : "Staff added");
      onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
             className="bg-white rounded-md p-6 w-full max-w-md space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">{isEdit ? "Edit staff" : "Add staff"}</div>
            <div className="font-heading text-xl">{isEdit ? form.name : "New delivery person"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={14}/></button>
        </div>
        <Fld label="Name *">
          <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inp}/>
        </Fld>
        <Fld label="Phone *">
          <input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inp}/>
        </Fld>
        <Fld label="Role">
          <select value={form.staff_type} onChange={e => setForm({...form, staff_type: e.target.value})} className={inp}>
            <option value="pharmacy_runner">Pharmacy runner</option>
            <option value="rental_logistics">Rental logistics</option>
            <option value="transport">Transport / logistics</option>
          </select>
        </Fld>
        <Fld label="Vehicle (optional)" hint="e.g., KL-01-AB-1234">
          <input value={form.vehicle} onChange={e => setForm({...form, vehicle: e.target.value})} className={inp}/>
        </Fld>
        <label className="inline-flex items-center gap-2 text-xs text-[#5C5A55]">
          <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="accent-[#2D5A27]"/>
          Available for new assignments
        </label>
        <div className="flex justify-end gap-2 pt-3 border-t border-[#F0ECE3]">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded border border-[#E5E2D9] text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="staff-submit"
                   className="h-10 px-5 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium disabled:opacity-60">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add staff"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ----- styles + small atoms -----
const inp = "w-full h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]";
const Fld = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-[#8A8780] mt-1">{hint}</div>}
  </label>
);
const Chip = ({ on, onClick, label }) => (
  <button onClick={onClick}
           className={`px-3 py-1.5 text-xs rounded border ${on ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#2D5A27]"}`}>
    {label}
  </button>
);
const DetRow = ({ Icon, k, v }) => (
  <div className="flex items-start gap-2">
    <Icon size={11} className="text-[#8A8780] mt-1 flex-none"/>
    <div className="flex-1 min-w-0">
      <span className="text-[#8A8780] uppercase tracking-[0.15em] text-[10px] mr-2">{k}</span>
      <span className="text-[#1A1A1A]">{v}</span>
    </div>
  </div>
);
const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[#F7F6F2] text-sm text-[#5C5A55]">Loading deliveries…</div>
);
